import { useQuery } from '@tanstack/react-query';
import { useMerchantScope } from '@noor/eats-api';
import { venueQueries } from '../api/venue-queries';
import type { VenueScope } from '../api/venue-api';

/**
 * The branch whose profile the Venue screen shows.
 *
 * `branchId` names a specific branch — the branches list links each row to its
 * own venue screen, so the id comes from the route rather than from the
 * session. Omitted, it falls back to the session's active branch, which is what
 * the un-parameterized `/eats/venue` route uses.
 */
export function useVenueScope(branchId?: string): VenueScope {
  const { branchId: activeBranchId } = useMerchantScope();
  return { restaurantId: branchId ?? activeBranchId ?? '' };
}

export function useVenue(branchId?: string) {
  const scope = useVenueScope(branchId);
  return useQuery(venueQueries.profile(scope));
}

/**
 * The same profile, read fresh — for the edit forms that seed from it.
 *
 * `staleTime: 0` so opening the hours modal re-reads instead of replaying the
 * copy the page happens to hold. The branch profile is MODERATED: a queued
 * change can be approved between the page load and the edit, and on the shared
 * 30s default that approval would be invisible in the form — which then saves
 * the pre-approval values back over it.
 *
 * Shares the page's cache entry (same key), so this refreshes what the page
 * shows too rather than fetching the branch twice.
 */
export function useVenueForEdit(branchId?: string) {
  const scope = useVenueScope(branchId);
  return useQuery({ ...venueQueries.profile(scope), staleTime: 0 });
}
